import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, CheckCircle2, Info, XCircle } from 'lucide-react';
import { apiClient } from '@/services/api';
import { useDesk } from '@/components/growth-desk/data/DeskData';
import { DESK } from '@/components/growth-desk/links';

/**
 * The bell: in-app notifications (campaigns ready for review, questions that need you,
 * failures, launches…). Polls every 30 seconds; opening the list marks it read.
 */

interface Note {
  id: string; kind: string; title: string; body?: string | null; link?: string | null; read: boolean; created_at: string | null;
  meta?: { production_id?: string } & Record<string, unknown>;
}

const POLL_MS = 30000;

const ago = (iso: string | null) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const ICON: Record<string, React.ReactNode> = {
  needs_input: <AlertTriangle className="h-4 w-4 text-bad" />,
  ready: <CheckCircle2 className="h-4 w-4 text-ok" />,
  failed: <XCircle className="h-4 w-4 text-bad" />,
  launch_problem: <XCircle className="h-4 w-4 text-bad" />,
};

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Note[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/v1/notifications/inbox', { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* the bell stays quiet if notifications are unavailable */ }
  }, []);

  useEffect(() => {
    load();
    timer.current = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer.current);
  }, [load]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread) {
      try { const { data } = await apiClient.post('/api/v1/notifications/inbox/read', {}); setUnread(data.unread || 0); } catch { /* ignore */ }
    }
  };

  const { needs } = useDesk();
  // Campaign notifications open the campaign's review on the desk; others keep their link.
  const linkOf = (n: Note) => (n.meta?.production_id ? DESK.review(n.meta.production_id) : n.link);
  const go = (to?: string | null) => {
    setOpen(false);
    if (to) navigate(to);
  };

  return (
    <div className="relative">
      <button type="button" onClick={toggle} aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} aria-expanded={open}
        className="relative grid h-10 w-10 place-items-center rounded-btn text-ink shadow-ring-line transition-shadow hover:shadow-ring-ink">
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-bad px-1 font-money text-[11px] text-card">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 flex max-h-[75vh] w-[min(25rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-panel bg-card shadow-decide" role="dialog" aria-label="Notifications">
            <div className="flex items-baseline justify-between gap-3 px-5 pb-2 pt-5">
              <span className="font-display text-card-title text-ink">Needs you <span className="font-money text-base text-ink-3">{needs.length}</span></span>
              <button type="button" onClick={() => go(DESK.needs)} className="text-small font-semibold text-brand-ink hover:underline">See all</button>
            </div>
            <div className="overflow-y-auto">
              {needs.length === 0 ? <p className="px-5 pb-3 text-small text-ink-3">Nothing needs you right now.</p> : (
                <ul className="m-0 list-none p-0">
                  {needs.slice(0, 4).map(n => (
                    <li key={n.id} className="border-t border-line px-5 py-3.5">
                      <span className={`mb-1.5 inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold ${n.urgent ? 'bg-bad-soft text-bad' : n.kind === 'review' ? 'bg-brand-soft text-brand-ink' : 'bg-inset text-navy-ink'}`}>{n.chip}</span>
                      <button type="button" onClick={() => go(n.to)} className="block w-full text-left text-sm font-semibold text-ink hover:underline">{n.title}</button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="border-t border-line px-5 pb-2 pt-4 text-label uppercase text-ink-3">Earlier · for your info</div>
              {items.length === 0 ? (
                <p className="px-5 pb-5 text-small text-ink-3">Nothing yet. We’ll tell you when campaigns are ready or need you.</p>
              ) : (
                <ul className="m-0 list-none p-0 pb-2">
                  {items.map(n => (
                    <li key={n.id}>
                      <button type="button" onClick={() => go(linkOf(n))}
                        className="grid w-full grid-cols-[20px_1fr_auto] gap-3 px-5 py-2.5 text-left hover:bg-ground">
                        <span className="mt-0.5">{ICON[n.kind] || <Info className="h-4 w-4 text-work" />}</span>
                        <span className="min-w-0">
                          <span className={`block text-sm text-ink ${n.read ? '' : 'font-semibold'}`}>{n.title}</span>
                          {n.body && <span className="block text-xs text-ink-2 line-clamp-2">{n.body}</span>}
                        </span>
                        <span className="whitespace-nowrap font-money text-[11px] text-ink-3">{ago(n.created_at)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
